import { useQuery } from '@tanstack/react-query'
import V3_FEE_READER_ABI from 'abis/V3FeeReader.json'
import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { V3_FEE_READER_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import { useMemo } from 'react'
import { ProcessedV3Position, V3PositionFeesBatchResult } from 'types/subgraph'
import { Address } from 'viem'

export function useV3PositionsFees(
  positions?: ProcessedV3Position[],
  options?: {
    enabled?: boolean
    refetchInterval?: number
  }
) {
  const { chainId } = useConnection()

  // Filter active positions (liquidity > 0) and prepare inputs
  const activePositions = useMemo(() => {
    if (!positions) return []

    return positions
      .filter((position) => position.sdkPosition.liquidity.toString() !== '0' && position.nftId && position.poolAddress)
      .map((position) => ({
        tokenId: position.nftId,
        poolAddress: position.poolAddress,
        liquidity: position.sdkPosition.liquidity.toString(),
      }))
  }, [positions])

  // Prepare contracts for multicall in batches
  const contracts = useMemo(() => {
    return activePositions.map((position) => ({
      address: V3_FEE_READER_ADDRESSES[chainId],
      abi: V3_FEE_READER_ABI as any,
      functionName: 'getFeesWithPool',
      args: [BigInt(position.tokenId), position.poolAddress as Address],
    }))
  }, [activePositions, chainId])

  const contractCall = useReadContractMultiple({
    contracts,
    allowFailure: true, // Allow individual calls to fail
    query: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: activePositions.length > 0 && options?.enabled !== false,
      refetchInterval: options?.refetchInterval ?? PORTFOLIO_REFETCH_POLICY.refetchInterval,
    },
  })

  // Process results into a map
  const feesData = useMemo((): V3PositionFeesBatchResult => {
    if (!contractCall.data || !activePositions.length) {
      return {}
    }

    const result: V3PositionFeesBatchResult = {}

    activePositions.forEach((position, index) => {
      const contractResult = contractCall.data[index]

      if (contractResult?.status === 'success' && contractResult.result) {
        const [amount0, amount1] = contractResult.result as [bigint, bigint]
        result[position.tokenId] = {
          amount0: amount0.toString(),
          amount1: amount1.toString(),
        }
      } else {
        // Handle failure
        const errorMessage =
          contractResult?.status === 'failure'
            ? contractResult.error?.message || 'Failed to fetch fees'
            : 'Unknown error'

        result[position.tokenId] = {
          amount0: '0',
          amount1: '0',
          error: errorMessage,
        }

        console.warn(`Failed to fetch fees for position ${position.tokenId}:`, errorMessage)
      }
    })

    return result
  }, [contractCall.data, activePositions])

  // Add zero fees for closed positions
  const allFeesData = useMemo((): V3PositionFeesBatchResult => {
    if (!positions) return feesData

    const result = { ...feesData }

    positions.forEach((position) => {
      if (position.sdkPosition.liquidity.toString() === '0' && !result[position.nftId]) {
        result[position.nftId] = {
          amount0: '0',
          amount1: '0',
        }
      }
    })

    return result
  }, [feesData, positions])

  return {
    data: allFeesData,
    isLoading: contractCall.isLoading,
    error: contractCall.error,
    activePositionsCount: activePositions.length,
    totalPositionsCount: positions?.length || 0,
    refetch: contractCall.refetch,
  }
}

export function useV3PositionsFeesBatched(
  positions?: ProcessedV3Position[],
  options?: {
    enabled?: boolean
    refetchInterval?: number
  }
) {
  const { chainId } = useConnection()
  const batchSize = 10

  // Split positions into batches
  const batches = useMemo(() => {
    if (!positions?.length) return []

    const activePositions = positions.filter((p) => p.sdkPosition.liquidity.toString() !== '0')
    const chunks: ProcessedV3Position[][] = []

    for (let i = 0; i < activePositions.length; i += batchSize) {
      chunks.push(activePositions.slice(i, i + batchSize))
    }

    return chunks
  }, [positions, batchSize])

  // Process batches sequentially with delays to avoid rate limits
  const batchResults = useQuery({
    queryKey: ['v3PositionsFeesBatched', positions?.map((p) => p.nftId).sort()],
    queryFn: async (): Promise<V3PositionFeesBatchResult> => {
      const allResults: V3PositionFeesBatchResult = {}

      // Add zero fees for closed positions first
      positions?.forEach((position) => {
        if (position.sdkPosition.liquidity.toString() === '0') {
          allResults[position.nftId] = {
            amount0: '0',
            amount1: '0',
          }
        }
      })

      // Process batches with delays
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i]
        if (batch.length === 0) continue

        try {
          // Add delay between batches to avoid rate limits
          if (i > 0) {
            await new Promise((resolve) => setTimeout(resolve, 100))
          }

          const contracts = batch.map((position) => ({
            address: V3_FEE_READER_ADDRESSES[chainId],
            abi: V3_FEE_READER_ABI,
            functionName: 'getFeesWithPool',
            args: [BigInt(position.nftId), position.poolAddress as Address],
          }))

          // This would need to be adapted to work with the actual multicall implementation
          // For now, we'll use individual calls in sequence
          const batchResults = await Promise.allSettled(
            contracts.map((contract) =>
              // Individual contract call - this would need actual implementation
              Promise.resolve([BigInt(0), BigInt(0)] as [bigint, bigint])
            )
          )

          batch.forEach((position, index) => {
            const result = batchResults[index]
            if (result.status === 'fulfilled') {
              const [amount0, amount1] = result.value
              allResults[position.nftId] = {
                amount0: amount0.toString(),
                amount1: amount1.toString(),
              }
            } else {
              allResults[position.nftId] = {
                amount0: '0',
                amount1: '0',
                error: result.reason?.message || 'Failed to fetch fees',
              }
            }
          })
        } catch (error) {
          console.error(`Error processing batch ${i}:`, error)
          // Add error results for this batch
          batch.forEach((position) => {
            allResults[position.nftId] = {
              amount0: '0',
              amount1: '0',
              error: 'Batch processing failed',
            }
          })
        }
      }

      return allResults
    },
    ...PORTFOLIO_REFETCH_POLICY,
    enabled: (positions?.length || 0) > 0 && options?.enabled !== false,
    refetchInterval: options?.refetchInterval ?? PORTFOLIO_REFETCH_POLICY.refetchInterval,
  })

  return {
    data: batchResults.data || {},
    isLoading: batchResults.isLoading,
    error: batchResults.error,
    batchCount: batches.length,
    totalPositions: positions?.length || 0,
  }
}

// Utility hook to get fees for a specific position from batch results
export function useV3PositionFeesFromBatch(tokenId: string, batchResults?: V3PositionFeesBatchResult) {
  return useMemo(() => {
    if (!batchResults || !tokenId) return undefined

    const fees = batchResults[tokenId]
    if (!fees) return undefined

    return {
      tokenId,
      amount0: fees.amount0,
      amount1: fees.amount1,
      error: fees.error,
      loading: false,
    }
  }, [tokenId, batchResults])
}
